import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function GalleryPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('전체');
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileEquipmentOpen, setMobileEquipmentOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const handleGoToMap = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('map');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const handleGoToSection = (sectionId: string) => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const categories = ['전체', '판금가공', '용접조립', '맞춤부품', '산업케이스'];

  const galleryItems = [
    {
      id: 1,
      title: '정밀 레이저 커팅 판금',
      category: '판금가공',
      description: '스테인리스 스틸 레이저 커팅 제품',
      image: 'precision laser cut stainless steel sheet metal parts with clean edges and intricate patterns, professional industrial manufacturing quality, detailed metalwork on white background'
    },
    {
      id: 2,
      title: '대형 구조물 용접',
      category: '용접조립',
      description: '산업용 대형 프레임 용접 조립',
      image: 'large industrial welded steel frame structure with professional welding joints, heavy duty metal fabrication with structural integrity, industrial setting photography'
    },
    {
      id: 3,
      title: '맞춤형 브라켓',
      category: '맞춤부품',
      description: '고객 요구사항 맞춤 제작 브라켓',
      image: 'custom metal brackets and mounting hardware with precise bending and holes, professional manufacturing quality with powder coating, clean product photography'
    },
    {
      id: 4,
      title: '전기 제어 케이스',
      category: '산업케이스',
      description: '전기 제어반용 금속 케이스',
      image: 'electrical control panel metal enclosure with door and mounting plate, professional industrial case with gray powder coating, clean white background'
    },
    {
      id: 5,
      title: '알루미늄 판금 가공',
      category: '판금가공',
      description: '알루미늄 소재 정밀 판금 제품',
      image: 'precision aluminum sheet metal parts with anodized finish and clean bends, lightweight metal fabrication with detailed craftsmanship, professional product photography'
    },
    {
      id: 6,
      title: '파이프 용접 조립',
      category: '용접조립',
      description: '스테인리스 파이프 용접 구조물',
      image: 'stainless steel pipe welding assembly with clean welds and polished finish, professional pipe fabrication with precise joints, industrial quality photography'
    },
    {
      id: 7,
      title: '정밀 기계 부품',
      category: '맞춤부품',
      description: '기계 장비용 맞춤 금속 부품',
      image: 'precision machined metal components for industrial machinery with tight tolerances, custom fabricated parts with detailed surface finish, professional manufacturing photography'
    },
    {
      id: 8,
      title: '방수 산업 케이스',
      category: '산업케이스',
      description: 'IP65 등급 방수 금속 케이스',
      image: 'waterproof industrial metal enclosure with sealed door and gasket, IP65 rated outdoor case with durable coating, professional product photography'
    },
    {
      id: 9,
      title: '복합 판금 제품',
      category: '판금가공',
      description: '다층 구조 판금 가공 제품',
      image: 'complex multi-layer sheet metal product with multiple bends and tabs, advanced metal fabrication with precise assembly, professional industrial photography'
    },
    {
      id: 10,
      title: 'TIG 용접 제품',
      category: '용접조립',
      description: '고품질 TIG 용접 마감 제품',
      image: 'TIG welded metal product with smooth clean welds and polished finish, high quality welding craftsmanship with precise joints, professional manufacturing photography'
    },
    {
      id: 11,
      title: '특수 형상 부품',
      category: '맞춤부품',
      description: '복잡한 형상의 맞춤 제작 부품',
      image: 'complex shaped custom metal parts with unique geometry and precise dimensions, specialized fabrication with detailed features, professional product photography'
    },
    {
      id: 12,
      title: '대형 산업 케이스',
      category: '산업케이스',
      description: '대형 장비용 금속 케이스',
      image: 'large industrial metal cabinet with multiple compartments and doors, heavy duty enclosure with professional finish, industrial equipment photography'
    },
    {
      id: 13,
      title: '스테인리스 판금',
      category: '판금가공',
      description: 'SUS304 스테인리스 판금 제품',
      image: 'SUS304 stainless steel sheet metal product with mirror finish and precise bending, corrosion resistant fabrication with clean edges, professional product photography'
    },
    {
      id: 14,
      title: '로봇 용접 제품',
      category: '용접조립',
      description: '로봇 용접 자동화 생산 제품',
      image: 'robotic welded metal product with consistent weld quality and precision, automated welding fabrication with uniform joints, professional industrial photography'
    },
    {
      id: 15,
      title: '항공 부품',
      category: '맞춤부품',
      description: '항공 산업용 정밀 금속 부품',
      image: 'aerospace precision metal components with tight tolerances and lightweight design, high quality aviation parts with detailed machining, professional manufacturing photography'
    }
  ];

  const filteredItems = activeCategory === '전체' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === activeCategory);

  const navLinks = [
    { href: '/#about', label: '회사소개', type: 'dropdown', subMenu: [
      { href: null, label: '회사소개', scrollTo: 'about' },
      { href: '/history', label: '연혁', scrollTo: null },
      { href: '/organization', label: '조직도', scrollTo: null },
      { href: null, label: '오시는 길', scrollTo: 'map' },
    ]},
    { href: null, label: '생산공정', type: 'section', scrollTo: 'process' },
    { href: '#equipment', label: '보유장비', type: 'dropdown', subMenu: [
      { href: '/equipment/production', label: '생산장비', scrollTo: null },
      { href: '/equipment/welding', label: '용접장비', scrollTo: null },
      { href: '/equipment/capability', label: '가공능력', scrollTo: null },
      { href: '/equipment/materials', label: '가공 가능자재', scrollTo: null },
    ]},
    { href: '/gallery', label: '제조물품', type: 'dropdown', subMenu: [
      { href: '/gallery', label: '갤러리', scrollTo: null },
    ]},
  ];

  return (
    <>
      <SEOHead
        title="제품/납품 사례 갤러리 | NS-TECH 판금가공"
        description="NS-TECH의 다양한 판금가공 제품 제작 사례와 납품 실적을 확인하세요. 레이저 커팅, 절곡, 용접 조립 등 정밀 금속 가공 포트폴리오를 소개합니다."
        keywords="판금가공 갤러리, 제품 사례, 납품 실적, 레이저 커팅 제품, 용접 조립, 맞춤 제작, 산업 케이스"
        ogType="website"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: '제품/납품 사례 갤러리',
            description: 'NS-TECH의 다양한 판금가공 제품 제작 사례와 납품 실적',
            url: `${import.meta.env.VITE_SITE_URL}/gallery`,
            publisher: {
              '@type': 'Organization',
              name: 'NS-TECH',
              url: import.meta.env.VITE_SITE_URL,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ImageGallery',
            name: 'NS-TECH 제품 갤러리',
            description: '판금가공, 용접조립, 맞춤부품, 산업케이스 제작 사례',
            url: `${import.meta.env.VITE_SITE_URL}/gallery`,
          },
        ]}
      />
      <div className="min-h-screen bg-white">
        {/* 네비게이션 */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
          <div className="w-full px-4 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="hidden lg:flex items-center gap-2">
                <Link to="/" className="flex items-center gap-2 shrink-0 mr-2">
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-12 w-auto object-contain"
                  />
                </Link>

                {navLinks.map((link) => (
                  link.type === 'dropdown' ? (
                    <div
                      key={link.label}
                      className="relative"
                      onMouseEnter={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(true);
                        if (link.label === '제조물품') setProductsDropdownOpen(true);
                        if (link.label === '회사소개') setAboutDropdownOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(false);
                        if (link.label === '제조물품') setProductsDropdownOpen(false);
                        if (link.label === '회사소개') setAboutDropdownOpen(false);
                      }}
                    >
                      <button className={`text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md flex items-center gap-1 cursor-pointer ${
                        link.label === '제조물품' ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                      }`}>
                        {link.label}
                        <i className={`ri-arrow-down-s-line transition-transform ${
                          (link.label === '보유장비' && equipmentDropdownOpen) ||
                          (link.label === '제조물품' && productsDropdownOpen) ||
                          (link.label === '회사소개' && aboutDropdownOpen)
                            ? 'rotate-180' : ''
                        }`}></i>
                      </button>
                      {((link.label === '보유장비' && equipmentDropdownOpen) ||
                        (link.label === '제조물품' && productsDropdownOpen) ||
                        (link.label === '회사소개' && aboutDropdownOpen)) && (
                        <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg py-2 min-w-[160px] border border-gray-100">
                          {link.subMenu?.map((subLink) => (
                            subLink.scrollTo ? (
                              <button
                                key={subLink.label}
                                onClick={() => handleGoToSection(subLink.scrollTo!)}
                                className="block w-full text-left px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                              >
                                {subLink.label}
                              </button>
                            ) : (
                              <Link
                                key={subLink.href}
                                to={subLink.href!}
                                className={`block px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap ${
                                  subLink.href === '/gallery' ? 'text-orange-500 font-semibold' : 'text-gray-700'
                                }`}
                              >
                                {subLink.label}
                              </Link>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  ) : link.type === 'section' ? (
                    <button
                      key={link.label}
                      onClick={() => handleGoToSection((link as any).scrollTo)}
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 cursor-pointer"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      key={link.href}
                      to={link.href!}
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                    >
                      {link.label}
                    </Link>
                  )
                ))}
              </div>

              <div className="flex lg:hidden items-center justify-between w-full">
                <Link to="/" className="flex items-center gap-2 shrink-0">
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-10 w-auto object-contain"
                  />
                </Link>
                <button
                  className="flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer relative z-[60]"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="메뉴 열기"
                >
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </button>
              </div>

              {/* 우측: 고객지원 버튼 (데스크탑만) */}
              <div className="hidden lg:flex items-center">
                <button
                  onClick={() => setShowContactModal(true)}
                  className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 cursor-pointer"
                >
                  고객지원
                </button>
              </div>
            </div>
          </div>

          {/* 모바일 오버레이 */}
          <div
            className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-40 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          {/* 모바일 메뉴 */}
          <div className={`lg:hidden fixed top-[60px] left-0 right-0 transition-all duration-300 ease-in-out z-50 ${mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}>
            <div className="bg-white shadow-xl border-t border-gray-100 px-5 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                link.type === 'dropdown' ? (
                  <div key={link.label}>
                    <button
                      onClick={() => {
                        if (link.label === '보유장비') setMobileEquipmentOpen(!mobileEquipmentOpen);
                        if (link.label === '제조물품') setMobileProductsOpen(!mobileProductsOpen);
                        if (link.label === '회사소개') setMobileAboutOpen(!mobileAboutOpen);
                      }}
                      className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50 flex items-center justify-between"
                    >
                      {link.label}
                      <i className={`ri-arrow-down-s-line transition-transform ${
                        (link.label === '보유장비' && mobileEquipmentOpen) ||
                        (link.label === '제조물품' && mobileProductsOpen) ||
                        (link.label === '회사소개' && mobileAboutOpen)
                          ? 'rotate-180' : ''
                      }`}></i>
                    </button>
                    {((link.label === '보유장비' && mobileEquipmentOpen) ||
                      (link.label === '제조물품' && mobileProductsOpen) ||
                      (link.label === '회사소개' && mobileAboutOpen)) && (
                      <div className="pl-4 mt-1 space-y-1">
                        {link.subMenu?.map((subLink) => (
                          subLink.scrollTo ? (
                            <button
                              key={subLink.label}
                              onClick={() => { setMobileMenuOpen(false); handleGoToSection(subLink.scrollTo!); }}
                              className="block w-full text-left text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </button>
                          ) : (
                            <Link
                              key={subLink.href}
                              to={subLink.href!}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : link.type === 'section' ? (
                  <button
                    key={link.label}
                    onClick={() => { setMobileMenuOpen(false); handleGoToSection((link as any).scrollTo); }}
                    className="text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href!}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </Link>
                )
              ))}
              {/* 모바일 고객지원 버튼 */}
              <button
                onClick={() => { setMobileMenuOpen(false); setShowContactModal(true); }}
                className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50"
              >
                고객지원
              </button>
            </div>
          </div>
        </nav>

        {/* 헤더 */}
        <div className="pt-20 md:pt-32 pb-8 md:pb-12 px-4 md:px-16 bg-gradient-to-b from-orange-50 to-white">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-2xl md:text-6xl font-bold text-gray-900 mb-2 md:mb-4">
              제품/납품 사례 갤러리
            </h1>
            <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
              NS-TECH의 다양한 제품 제작 사례와 납품 실적을 확인하세요
            </p>
          </div>
        </div>

        {/* 카테고리 필터 */}
        <div className="sticky top-[60px] z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 md:px-16 py-3 md:py-4">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-0.5">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-1.5 md:px-5 md:py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === category
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 갤러리 그리드 */}
        <div className="py-6 md:py-16 px-4 md:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
              {(activeCategory === '전체'
                ? [
                    { id: 1, title: '정밀 레이저 커팅 판금', category: '판금가공', description: '스테인리스 스틸 레이저 커팅 제품' },
                    { id: 2, title: '대형 구조물 용접', category: '용접조립', description: '산업용 대형 프레임 용접 조립' },
                    { id: 3, title: '맞춤형 브라켓', category: '맞춤부품', description: '고객 요구사항 맞춤 제작 브라켓' },
                    { id: 4, title: '전기 제어 케이스', category: '산업케이스', description: '전기 제어반용 금속 케이스' },
                    { id: 5, title: '알루미늄 판금 가공', category: '판금가공', description: '알루미늄 소재 정밀 판금 제품' },
                    { id: 6, title: '파이프 용접 조립', category: '용접조립', description: '스테인리스 파이프 용접 구조물' },
                    { id: 7, title: '정밀 기계 부품', category: '맞춤부품', description: '기계 장비용 맞춤 금속 부품' },
                    { id: 8, title: '방수 산업 케이스', category: '산업케이스', description: 'IP65 등급 방수 금속 케이스' },
                    { id: 9, title: '복합 판금 제품', category: '판금가공', description: '다층 구조 판금 가공 제품' },
                    { id: 10, title: 'TIG 용접 제품', category: '용접조립', description: '고품질 TIG 용접 마감 제품' },
                    { id: 11, title: '특수 형상 부품', category: '맞춤부품', description: '복잡한 형상의 맞춤 제작 부품' },
                    { id: 12, title: '대형 산업 케이스', category: '산업케이스', description: '대형 장비용 금속 케이스' },
                    { id: 13, title: '스테인리스 판금', category: '판금가공', description: 'SUS304 스테인리스 판금 제품' },
                    { id: 14, title: '로봇 용접 제품', category: '용접조립', description: '로봇 용접 자동화 생산 제품' },
                    { id: 15, title: '항공 부품', category: '맞춤부품', description: '항공 산업용 정밀 금속 부품' },
                  ]
                : [
                    { id: 1, title: '정밀 레이저 커팅 판금', category: '판금가공', description: '스테인리스 스틸 레이저 커팅 제품' },
                    { id: 2, title: '대형 구조물 용접', category: '용접조립', description: '산업용 대형 프레임 용접 조립' },
                    { id: 3, title: '맞춤형 브라켓', category: '맞춤부품', description: '고객 요구사항 맞춤 제작 브라켓' },
                    { id: 4, title: '전기 제어 케이스', category: '산업케이스', description: '전기 제어반용 금속 케이스' },
                    { id: 5, title: '알루미늄 판금 가공', category: '판금가공', description: '알루미늄 소재 정밀 판금 제품' },
                    { id: 6, title: '파이프 용접 조립', category: '용접조립', description: '스테인리스 파이프 용접 구조물' },
                    { id: 7, title: '정밀 기계 부품', category: '맞춤부품', description: '기계 장비용 맞춤 금속 부품' },
                    { id: 8, title: '방수 산업 케이스', category: '산업케이스', description: 'IP65 등급 방수 금속 케이스' },
                    { id: 9, title: '복합 판금 제품', category: '판금가공', description: '다층 구조 판금 가공 제품' },
                    { id: 10, title: 'TIG 용접 제품', category: '용접조립', description: '고품질 TIG 용접 마감 제품' },
                    { id: 11, title: '특수 형상 부품', category: '맞춤부품', description: '복잡한 형상의 맞춤 제작 부품' },
                    { id: 12, title: '대형 산업 케이스', category: '산업케이스', description: '대형 장비용 금속 케이스' },
                    { id: 13, title: '스테인리스 판금', category: '판금가공', description: 'SUS304 스테인리스 판금 제품' },
                    { id: 14, title: '로봇 용접 제품', category: '용접조립', description: '로봇 용접 자동화 생산 제품' },
                    { id: 15, title: '항공 부품', category: '맞춤부품', description: '항공 산업용 정밀 금속 부품' },
                  ].filter(item => item.category === activeCategory)
              ).map((item) => (
                <div 
                  key={item.id} 
                  className="group cursor-pointer bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-sm md:shadow-md hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative h-40 md:h-72 overflow-hidden bg-white">
                    <img 
                      src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                      alt={item.title}
                      className="w-full h-full object-contain p-4 md:p-8 group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-3 md:p-5 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        <p className="text-xs md:text-sm opacity-90">{item.description}</p>
                      </div>
                    </div>
                    <span className="absolute top-2 right-2 md:top-4 md:right-4 px-2 py-0.5 md:px-3 md:py-1 bg-orange-500 text-white text-xs font-semibold rounded-full">
                      {item.category}
                    </span>
                  </div>
                  <div className="p-3 md:p-5">
                    <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2 leading-snug">
                      {item.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-500 hidden sm:block">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 하단 CTA */}
        <div className="py-10 md:py-16 px-4 md:px-16 bg-gradient-to-b from-white to-orange-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-4">
              맞춤 제작 문의하기
            </h2>
            <p className="text-sm md:text-lg text-gray-600 mb-6 md:mb-8">
              고객님의 요구사항에 맞는 제품을 제작해드립니다
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
              <Link 
                to="/#quote"
                className="px-6 py-3 md:px-8 md:py-4 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer text-sm md:text-base"
              >
                견적 문의하기
              </Link>
              <Link 
                to="/"
                className="px-6 py-3 md:px-8 md:py-4 bg-white text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors border-2 border-gray-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
              >
                홈으로 돌아가기
              </Link>
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <footer className="bg-orange-500 text-white py-8 px-5">
          <div className="max-w-5xl mx-auto text-center">
            <img
              src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
              alt="NS-TECH Logo"
              className="h-10 w-auto mx-auto mb-4 brightness-0 invert"
            />
            <p className="text-white/80 text-xs mb-1">© 2025 NS-TECH. All rights reserved.</p>
            <p className="text-white/60 text-xs">경기도 포천시 소흘읍 소흘로 11 | Tel: 031-541-1902 | Email: nstech01@naver.com</p>
          </div>
        </footer>

        {/* 모바일 플로팅 전화 버튼 */}
        <a
          href="tel:031-541-1902"
          className="lg:hidden fixed bottom-24 right-4 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white pl-3 pr-4 py-2.5 rounded-full shadow-xl transition-all active:scale-95 cursor-pointer"
          aria-label="전화 연결"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-fill text-white text-base"></i>
          </div>
          <span className="text-xs font-bold whitespace-nowrap">전화문의</span>
        </a>

        {/* 연락처 모달 */}
        {showContactModal && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setShowContactModal(false)}
          >
            <div
              className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 relative shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowContactModal(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="닫기"
              >
                <i className="ri-close-line text-gray-600 text-xl"></i>
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-orange-500 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">견적 문의</h3>
                <p className="text-sm text-gray-500">편하신 방법으로 연락주세요</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-building-line text-orange-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">회사명</p>
                    <p className="text-base font-semibold text-gray-900">NS-TECH</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">전화번호</p>
                    <p className="text-base font-semibold text-gray-900">031-541-1902</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-printer-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">팩스</p>
                    <p className="text-base font-semibold text-gray-900">031-541-1903</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">이메일</p>
                    <p className="text-base font-semibold text-gray-900">nstech01@naver.com</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">운영시간</p>
                    <p className="text-base font-semibold text-gray-900">AM 08:00 ~ PM 18:00</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <a
                  href="tel:031-541-1902"
                  className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line"></i>
                  <span>전화하기</span>
                </a>
                <a
                  href="mailto:nstech01@naver.com"
                  className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  <span>메일보내기</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}